import React, { useRef, useEffect } from "react";
import DragIcon from "../../icons/svg/drag_and_drop.svg";

/**
 * ResizableContainer - A truly reusable container with drag-to-resize functionality
 *
 * Key Features:
 * - Self-contained: Manages its own state without causing parent re-renders
 * - Performance optimized: Uses refs and direct DOM manipulation
 * - No dependencies on parent state changes
 * - Simple API with sensible defaults
 * - Supports both vertical and horizontal resizing
 *
 * Usage:
 * Vertical (default):
 * <ResizableContainer defaultHeight={300} minHeight={150} maxHeight={600}>
 *   <YourComponent />
 * </ResizableContainer>
 *
 * Horizontal:
 * <ResizableContainer direction="horizontal" defaultWidth={400} minWidth={200} maxWidth={800}>
 *   <YourComponent />
 * </ResizableContainer>
 */
const ResizableContainer = ({
  children,
  defaultHeight = 300,
  minHeight = 150,
  maxHeight = 600,
  defaultWidth = 400,
  minWidth = 200,
  maxWidth = 800,
  direction = "vertical", // "vertical" or "horizontal"
}) => {
  const containerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const startXRef = useRef(0);
  const startHeightRef = useRef(0);
  const startWidthRef = useRef(0);
  const hasUserResizedRef = useRef(false); // Track if user manually resized
  const userResizedHeightRef = useRef(null); // Store user's custom height
  const userResizedWidthRef = useRef(null); // Store user's custom width

  const isVertical = direction === "vertical";
  const isHorizontal = direction === "horizontal";

  useEffect(() => {
    // Only set initial dimensions if user hasn't manually resized
    if (containerRef.current && !hasUserResizedRef.current) {
      if (isVertical) {
        containerRef.current.style.height = `${defaultHeight}px`;
      } else if (isHorizontal) {
        containerRef.current.style.width = `${defaultWidth}px`;
      }
    } else if (containerRef.current) {
      // If user has resized, use their custom dimensions even on tab change
      if (isVertical && userResizedHeightRef.current) {
        containerRef.current.style.height = `${userResizedHeightRef.current}px`;
      } else if (isHorizontal && userResizedWidthRef.current) {
        containerRef.current.style.width = `${userResizedWidthRef.current}px`;
      }
    }
  }, [defaultHeight, defaultWidth, isVertical, isHorizontal]);

  const startDrag = (clientX, clientY) => {
    if (!containerRef.current) return;

    isDraggingRef.current = true;

    if (isVertical) {
      startYRef.current = clientY;
      startHeightRef.current = containerRef.current.offsetHeight;
      document.body.style.cursor = "ns-resize";
    } else if (isHorizontal) {
      startXRef.current = clientX;
      startWidthRef.current = containerRef.current.offsetWidth;
      document.body.style.cursor = "ew-resize";
    }

    containerRef.current.style.userSelect = "none";
  };

  const handleMouseDown = e => {
    e.preventDefault();
    e.stopPropagation();
    startDrag(e.clientX, e.clientY);
  };

  const handleTouchStart = e => {
    e.stopPropagation();
    const touch = e.touches[0];
    startDrag(touch.clientX, touch.clientY);
  };

  useEffect(() => {
    const moveDrag = (clientX, clientY) => {
      if (!isDraggingRef.current || !containerRef.current) return;

      if (isVertical) {
        const deltaY = startYRef.current - clientY;
        const newHeight = startHeightRef.current + deltaY;
        const constrainedHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
        containerRef.current.style.height = `${constrainedHeight}px`;
      } else if (isHorizontal) {
        const deltaX = clientX - startXRef.current;
        const newWidth = startWidthRef.current + deltaX;
        const constrainedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
        containerRef.current.style.width = `${constrainedWidth}px`;
      }
    };

    const endDrag = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        document.body.style.cursor = "";
        if (containerRef.current) {
          containerRef.current.style.userSelect = "";
          if (isVertical) {
            userResizedHeightRef.current = parseInt(containerRef.current.style.height);
          } else if (isHorizontal) {
            userResizedWidthRef.current = parseInt(containerRef.current.style.width);
          }
          hasUserResizedRef.current = true;
        }
      }
    };

    const handleMouseMove = e => moveDrag(e.clientX, e.clientY);
    const handleMouseUp = () => endDrag();
    const handleTouchMove = e => {
      if (!isDraggingRef.current) return;
      e.preventDefault();
      const touch = e.touches[0];
      moveDrag(touch.clientX, touch.clientY);
    };
    const handleTouchEnd = () => endDrag();

    // Attach global listeners for both mouse and touch
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [minHeight, maxHeight, minWidth, maxWidth, isVertical, isHorizontal]);

  return (
    <div
      ref={containerRef}
      className={`border border-gray-200 bg-white flex ${
        isVertical ? "flex-col" : "flex-row"
      } overflow-hidden relative`}
    >
      {/* Drag Handle */}
      <div
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        className={`flex justify-center items-center sticky z-10 select-none bg-transparent ${
          isVertical
            ? "h-[12px] cursor-ns-resize w-full top-0"
            : "w-[12px] cursor-ew-resize h-full left-0"
        }`}
        title="Drag to resize"
      >
        <img
          src={DragIcon}
          alt="Drag to resize"
          className={`pointer-events-none opacity-60 ${isHorizontal ? "rotate-90" : ""}`}
        />
      </div>

      {/* Content Area */}
      <div className="flex-1 min-h-0 min-w-0 px-2 overflow-hidden">{children}</div>
    </div>
  );
};

export default ResizableContainer;
